<script setup lang="ts">
import {
  UserCircleIcon,
  HeartIcon,
  UserIcon,
  UsersIcon,
  ClockIcon,
  ClipboardListIcon,
  DocumentReportIcon,
  LogoutIcon,
  PhotographIcon,
  DocumentTextIcon,
  CurrencyPoundIcon,
  QuestionMarkCircleIcon,
  DocumentDownloadIcon,
  CollectionIcon
} from '@heroicons/vue/solid';
import { RoleConstants } from '~/types/global';
import DynamicIcon from '~/components/ui/icons/DynamicIcon.vue';

const { logout, userData } = useAuth();

const { hasPermission } = usePermissions();

const canManageUsers = computed(() => hasPermission(RoleConstants.Users.UpdateUsers));
const canReadOrders = computed(() => hasPermission(RoleConstants.Orders.ReadOrders));
const canReadPriceLists = computed(() => hasPermission(RoleConstants.Pricing.ViewPriceLists));
const canReadStatements = computed(() => hasPermission(RoleConstants.Billing.ReadStatements));
const canReadInvoices = computed(() => hasPermission(RoleConstants.Billing.ReadInvoices));

const siteSettingsData = inject('siteSettings');

const tradeResources = computed(() => {
  return siteSettingsData?.tradeResources || [];
});

const {
  public: { favouritesEnabled }
} = useRuntimeConfig();
</script>

<template>
  <div class="hidden flex-row items-center gap-4 md:flex">
    <DropdownMenu>
      <DropdownMenuTrigger class="group transition-colors hover:text-gunmetal" aria-label="Account menu">
        <UserCircleIcon class="size-6" aria-hidden="true" />
        <OverlayBlur />
      </DropdownMenuTrigger>
      <DropdownMenuContent>
        <DropdownMenuLabel>Hello {{ userData?.humanName?.firstName }}</DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem>
          <NuxtLink
            to="/account"
            class="flex w-full flex-row items-center gap-2"
            aria-label="View your account details"
          >
            <UserIcon class="size-4" aria-hidden="true" />
            Account details
          </NuxtLink>
        </DropdownMenuItem>
        <DropdownMenuItem v-if="canManageUsers">
          <NuxtLink
            to="/account/manage-users"
            class="flex w-full flex-row items-center gap-2"
            aria-label="Manage user accounts"
          >
            <UsersIcon class="size-4" aria-hidden="true" />
            Manage users
          </NuxtLink>
        </DropdownMenuItem>
        <DropdownMenuItem>
          <NuxtLink to="/account/unsubmitted" class="flex w-full flex-row items-center gap-2">
            <CurrencyPoundIcon class="size-4" aria-hidden="true" />
            Unsubmitted
          </NuxtLink>
        </DropdownMenuItem>
        <DropdownMenuItem>
          <NuxtLink
            to="/account/order-history"
            class="flex w-full flex-row items-center gap-2"
            aria-label="View order history"
            v-if="canReadOrders"
          >
            <ClockIcon class="size-4" aria-hidden="true" />
            Order history
          </NuxtLink>
        </DropdownMenuItem>
        <DropdownMenuItem v-if="canReadInvoices">
          <NuxtLink to="/account/invoices" class="flex w-full flex-row items-center gap-2">
            <ClipboardListIcon class="size-4" aria-hidden="true" />
            Invoices
          </NuxtLink>
        </DropdownMenuItem>
        <DropdownMenuItem v-if="canReadStatements" as-child>
          <NuxtLink to="/account/statements" class="flex w-full flex-row items-center gap-2">
            <DocumentReportIcon class="size-4" aria-hidden="true" />
            Statements
          </NuxtLink>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem @click="() => logout()" aria-label="Log out of your account">
          <LogoutIcon class="size-4" aria-hidden="true" />
          Logout
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
    <DropdownMenu v-if="tradeResources.length > 0">
      <DropdownMenuTrigger class="group transition-colors hover:text-gunmetal" aria-label="Trade resources menu">
        <DocumentDownloadIcon class="size-6" aria-hidden="true" />
        <OverlayBlur />
      </DropdownMenuTrigger>
      <DropdownMenuContent>
        <DropdownMenuLabel>Trade resources</DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem v-for="(item, index) in tradeResources" :key="`trade-resource-link-${index}`">
          <NuxtLink :href="item.link.href" :target="item?.link?.target" class="flex w-full flex-row items-center gap-2">
            <DynamicIcon v-if="item.iconAlias" :name="item.iconAlias" class="size-4" aria-hidden="true" />
            <CollectionIcon v-else class="size-4" aria-hidden="true" />
            {{ item?.link?.title }}
          </NuxtLink>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
    <DropdownMenu v-if="favouritesEnabled">
      <DropdownMenuTrigger class="group transition-colors hover:text-gunmetal" aria-label="Favourites">
        <HeartIcon class="size-6" aria-hidden="true" />
        <OverlayBlur />
      </DropdownMenuTrigger>
      <DropdownMenuContent>
        <DropdownMenuLabel>Favourites</DropdownMenuLabel>
      </DropdownMenuContent>
    </DropdownMenu>
  </div>
</template>
